/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module SharedModule
 */

import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    Output,
    TemplateRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { FocusableOption } from '@angular/cdk/a11y';
import { AviTooltipModule } from 'nsx-alb-ui-components';
import { TooltipModule } from 'ng/modules/tooltip/tooltip.module';
import {
    SignpostPosition,
    signpostPositionMap,
    SIGNPOST_LEFT_CONNECTED_POSITION,
    SIGNPOST_RIGHT_CONNECTED_POSITION,
} from 'ng/modules/tooltip/components/signpost/signpost.constants';

import {
    DropdownModelSingleValue,
    IAviDropdownOption,
} from '../../avi-dropdown.types';

import './avi-dropdown-option.component.less';

const RIGHT_CONNECTED_POSITION: ConnectedPosition = {
    offsetX: 10,
    originX: 'end',
    originY: 'center',
    overlayX: 'start',
    overlayY: 'center',
};

const LEFT_CONNECTED_POSITION: ConnectedPosition = {
    offsetX: -10,
    originX: 'start',
    originY: 'center',
    overlayX: 'end',
    overlayY: 'center',
};

const tooltipPositionsPriority: ConnectedPosition[] = [
    RIGHT_CONNECTED_POSITION,
    LEFT_CONNECTED_POSITION,
];

const caretClassNameMap = new Map([
    [RIGHT_CONNECTED_POSITION, 'avi-dropdown-option__tooltip--right'],
    [LEFT_CONNECTED_POSITION, 'avi-dropdown-option__tooltip--left'],
]);

/**
 * Map to convert the left and right connected positions to signpost connected positions.
 */
const tooltipPositionToSignpostPosition = new Map([
    [LEFT_CONNECTED_POSITION, SIGNPOST_LEFT_CONNECTED_POSITION],
    [RIGHT_CONNECTED_POSITION, SIGNPOST_RIGHT_CONNECTED_POSITION],
]);

/**
 * @description Component for a dropdown option.
 * @author alextsg, Alex Klyuev
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        tabindex: '-1',
        role: 'list-item',
    },
    selector: 'avi-dropdown-option',
    templateUrl: './avi-dropdown-option.component.html',
    standalone: true,
    imports: [
        CommonModule,
        AviTooltipModule,
        TooltipModule,
    ],
})
export class AviDropdownOptionComponent<TDropdownOptionValue = DropdownModelSingleValue>
implements FocusableOption {
    /**
     * Option value.
     */
    @Input()
    public value?: TDropdownOptionValue;

    /**
     * Text to be displayed in the option.
     */
    @Input()
    public label?: IAviDropdownOption['label'];

    /**
     * Text to be displayed in the tooltip.
     */
    @Input()
    public tooltipText?: IAviDropdownOption['tooltip'];

    @Input()
    public optionTemplateRef?: TemplateRef<HTMLElement>;

    /**
     * Optional signpost templateRef.
     */
    @Input()
    public optionSignpostTemplateRef?: TemplateRef<HTMLElement>;

    /**
     * Optional data object that is passed to the templateRef as templateContext.
     */
    @Input()
    public optionData?: Record<string, any>;

    /**
     * Event emitters for mouse events on this option.
     */
    @Output()
    public onMouseEnter = new EventEmitter<void>();

    @Output()
    public onMouseMove = new EventEmitter<void>();

    @Output()
    public onSelect = new EventEmitter<void>();

    public tooltipPositionsPriority: ConnectedPosition[] = tooltipPositionsPriority;
    public caretClassName = caretClassNameMap.get(tooltipPositionsPriority[0]);

    /**
     * Decide signpost position relative to its parent.
     */
    public signpostPosition: SignpostPosition;

    constructor(public readonly elementRef: ElementRef) {}

    /**
     * Listeners for mouse events on this option.
     */
    @HostListener('mouseenter')
    public handleMouseEnter(): void {
        this.onMouseEnter.emit();
    }

    @HostListener('mousemove')
    public handleMouseMove(): void {
        this.onMouseMove.emit();
    }

    /**
     * Handler for the position change of the tooltip content. Used to reposition the caret.
     */
    public handlePositionChange(connectedPosition: ConnectedPosition): void {
        const signpostConnectedPosition = tooltipPositionToSignpostPosition.get(connectedPosition);

        this.signpostPosition = signpostPositionMap.get(signpostConnectedPosition);
        this.caretClassName = caretClassNameMap.get(connectedPosition);
    }

    /**
     * Focus this element. Needed to implement Angular CDK's FocusableOption.
     */
    public focus(): void {
        this.elementRef.nativeElement.focus();
    }

    /**
     * Handle select dropdown option.
     */
    public handleSelect(): void {
        this.onSelect.emit();
    }
}
